import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AdminProvider } from './contexts/AdminContext';
import { SocketProvider } from './contexts/SocketContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/admin/AdminLayout';

// Pages 
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import Dashboard from './pages/Dashboard';
import JobDetailsPage from './pages/jobs/JobDetailsPage';
import PostJobPage from './pages/jobs/PostJobPage';
import JobPostingsPage from './pages/employer/JobPostingsPage';
import ApplicationsPage from './pages/employer/ApplicationsPage';
import AnalyticsPage from './pages/employer/AnalyticsPage';
import BrowseCandidatesPage from './pages/employer/BrowseCandidatesPage';
import CoursesPage from './pages/CoursesPage';
import JobsPage from './pages/JobsPage';
import CourseDetailsPage from './pages/courses/CourseDetailsPage';
import QuizPage from './pages/quizzes/QuizPage';
import RankingsPage from './pages/RankingsPage';
import TalentPage from './pages/TalentPage';
import ProfilePage from './pages/ProfilePage';
import StudentProfileViewPage from './pages/StudentProfileViewPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PreciseApplicationPath from './pages/PreciseApplicationPath';
import ManageSubscriptionPage from './pages/ManageSubscriptionPage';
import MessagesPage from './pages/MessagesPage';
import NotFoundPage from './pages/NotFoundPage';
import EmployerViewPage from './components/employer/EmployerViewPage';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import JobManagement from './pages/admin/JobManagement';
import CourseManagement from './pages/admin/CourseManagement';
import VerificationQueue from './pages/admin/VerificationQueue';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import SubscriptionPlans from './pages/admin/SubscriptionPlans';
import ContentModeration from './pages/admin/ContentModeration';
import BackgroundChecks from './pages/admin/BackgroundChecks';
import Analytics from './pages/admin/Analytics';
import Reports from './pages/admin/Reports';
import SystemSettings from './pages/admin/SystemSettings';
import AdminManagement from './pages/admin/AdminManagement';
import EmailManagement from './pages/admin/EmailManagement';
import SupportTickets from './pages/admin/SupportTickets';
import ContactInquiries from './pages/admin/ContactInquiries';

// Support & Contact
import SupportPage from './pages/SupportPage';
import ContactUsPage from './pages/ContactUsPage';

// Legal
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Onboarding
import OnboardingPage from './pages/OnboardingPage';

function App() {
  return (
    <Router>
      <AuthProvider>
        <AdminProvider>
          <SocketProvider>
          <div className="App">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#f87171',
                    secondary: '#fff',
                  },
                },
              }}
            />
            
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="jobs" element={<JobManagement />} />
                <Route path="courses" element={<CourseManagement />} />
                <Route path="verifications" element={<VerificationQueue />} />
                <Route path="moderation" element={<ContentModeration />} />
                <Route path="background-checks" element={<BackgroundChecks />} />
                <Route path="subscription-plans" element={<SubscriptionPlans />} />
                <Route path="subscriptions" element={<SubscriptionManagement />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="reports" element={<Reports />} />
                <Route path="emails" element={<EmailManagement />} />
                <Route path="support" element={<SupportTickets />} />
                <Route path="contact" element={<ContactInquiries />} />
                <Route path="settings" element={<SystemSettings />} />
                <Route path="admins" element={<AdminManagement />} />
              </Route>
              
              {/* Onboarding Route */}
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              } />
              
              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/precise-application-path"
                element={
                  <ProtectedRoute requiredUserType="student">
                    <Layout>
                      <PreciseApplicationPath />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/jobs"
                element={
                  <Layout>
                    <JobsPage />
                  </Layout>
                }
              />
              
              <Route
                path="/jobs/:id"
                element={
                  <Layout>
                    <JobDetailsPage />
                  </Layout>
                }
              />
              
              <Route
                path="/post-job"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <PostJobPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer/job-posting"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <PostJobPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/employer/job-postings"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <JobPostingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer/applications"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <ApplicationsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer/analytics"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <AnalyticsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer/candidates"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <BrowseCandidatesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/courses"
                element={
                  <Layout>
                    <CoursesPage />
                  </Layout>
                }
              />
              
              <Route
                path="/courses/:id"
                element={
                  <Layout>
                    <CourseDetailsPage />
                  </Layout>
                }
              />
              
              <Route
                path="/quiz/:id"
                element={
                  <ProtectedRoute requiredUserType="student">
                    <Layout>
                      <QuizPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/rankings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RankingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/talent"
                element={
                  <Layout>
                    <TalentPage />
                  </Layout>
                }
              />
              
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProfilePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/profile/student/:userId"
                element={
                  <ProtectedRoute requiredUserType="employer">
                    <Layout>
                      <StudentProfileViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              {/* Messages Routes */}
              {/* Public: MessagesPage renders a typable preview when signed out. */}
              <Route
                path="/messages"
                element={
                  <Layout>
                    <MessagesPage />
                  </Layout>
                }
              />
              
              <Route
                path="/messages/:conversationId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <MessagesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              {/* Subscription Routes */}
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SubscriptionPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/manage-subscription"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ManageSubscriptionPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              <Route
                path="/employer-view/:employerId"
                element={
                  <ProtectedRoute requiredUserType="student">
                    <Layout>
                      <EmployerViewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              {/* Support Route */}
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SupportPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              
              {/* Contact Us Route */}
              <Route
                path="/contact"
                element={
                  <Layout>
                    <ContactUsPage />
                  </Layout>
                }
              />
              
              {/* Legal Pages */}
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              
              {/* Redirect /app to /dashboard for legacy URLs */}
              <Route path="/app" element={<Navigate to="/dashboard" replace />} />
              
              {/* 404 Route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
          </SocketProvider>
        </AdminProvider>
      </AuthProvider>
    </Router>
  );
}

export default App; 